import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { blogPosts, blogThumbnails } from "@/data/blogData";
import ReactMarkdown from "react-markdown";
import ScrollReveal from "@/components/ScrollReveal";
import { ArrowLeft, Clock } from "lucide-react";
import { motion } from "framer-motion";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <h1 className="font-heading text-3xl font-bold mb-4">Post Not Found</h1>
          <Button onClick={() => navigate("/blog")}>Back to Blog</Button>
        </div>
      </Layout>
    );
  }

  const thumbnail = slug ? blogThumbnails[slug] : undefined;
  const wordCount = post.content.split(/\s+/).length;
  const readTime = Math.max(1, Math.ceil(wordCount / 230));

  return (
    <Layout>
      <SEOHead title={post.metaTitle} description={post.metaDescription} path={`/blog/${post.slug}`} />

      {/* Hero banner with thumbnail */}
      {thumbnail && (
        <div className="relative w-full h-[280px] sm:h-[360px] md:h-[420px] overflow-hidden">
          <img src={thumbnail} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>
      )}

      <article className={`pb-20 ${thumbnail ? "-mt-24 relative z-10" : "pt-20"}`}>
        <div className="container max-w-3xl">
          {/* Back button */}
          <ScrollReveal>
            <button
              onClick={() => navigate("/blog")}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6 group"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </button>
          </ScrollReveal>

          {/* Header */}
          <ScrollReveal delay={0.05}>
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Badge variant="secondary" className="capitalize font-medium">{post.category}</Badge>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  {readTime} min read
                </span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-[2.75rem] font-bold leading-tight mb-5">
                {post.title}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {post.hook}
              </p>
            </div>
          </ScrollReveal>

          {/* Divider */}
          <ScrollReveal delay={0.1}>
            <div className="h-px bg-border mb-10" />
          </ScrollReveal>

          {/* Content */}
          <ScrollReveal delay={0.15}>
            <div className="prose prose-base md:prose-lg prose-invert max-w-none
              prose-headings:font-heading prose-headings:font-bold prose-headings:tracking-tight
              prose-h2:text-2xl prose-h2:md:text-[1.75rem] prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:leading-[1.8] prose-p:text-foreground/85 prose-p:mb-5
              prose-li:leading-[1.8] prose-li:text-foreground/85 prose-li:marker:text-primary
              prose-strong:text-foreground prose-strong:font-semibold
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-ul:my-5 prose-ol:my-5
              prose-blockquote:border-l-primary prose-blockquote:bg-secondary/50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
            ">
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </div>
          </ScrollReveal>

          {/* CTA */}
          <ScrollReveal delay={0.2}>
            <motion.div
              whileHover={{ scale: 1.01 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="mt-14 p-8 md:p-10 glass-card rounded-2xl text-center border-primary/20"
              style={{ background: "linear-gradient(135deg, rgba(0,217,255,0.05), rgba(79,70,229,0.05))" }}
            >
              <h3 className="font-heading font-bold text-xl md:text-2xl mb-3">Ready to automate your business?</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Book a free consultation and discover how an AI chatbot can capture more leads, save you time, and grow your revenue.
              </p>
              <Button
                size="lg"
                className="border-0 px-8"
                style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                onClick={() => navigate("/contact")}
              >
                <span className="text-white font-semibold">Book Free Consultation →</span>
              </Button>
            </motion.div>
          </ScrollReveal>

          {/* Related posts suggestion */}
          <ScrollReveal delay={0.25}>
            <div className="mt-12 pt-8 border-t border-border">
              <h4 className="font-heading font-bold text-lg mb-4">Keep Reading</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                {blogPosts
                  .filter((p) => p.slug !== slug && p.category === post.category)
                  .slice(0, 2)
                  .map((related) => (
                    <button
                      key={related.slug}
                      onClick={() => navigate(`/blog/${related.slug}`)}
                      className="text-left p-4 rounded-xl glass-card-hover group"
                    >
                      <Badge variant="secondary" className="capitalize text-[10px] mb-2">{related.category}</Badge>
                      <p className="font-heading font-semibold text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">
                        {related.title}
                      </p>
                    </button>
                  ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </article>
    </Layout>
  );
};

export default BlogPost;
